import { FC } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from 'hooks/useTranslate';
import NumberFormat from 'react-number-format';

import draftTypes from 'constants/draftTypes';
import { numberFormat } from 'constants/formats';

import Radio from 'modules/_shared/components/Radio';
import MandatoryField from 'modules/_shared/components/MandatoryField';
import proportionalityTypes from 'modules/drafts/pages/DraftCapitalIncrease/proportionalityTypes';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';

import PartnerSection from './PartnerSection';
import CapitalIncreaseOptionsSection from './CapitalIncraseOptionsSection';
import { useAddDraftInvestorModal } from '../context/AddDraftInvestorModalContext';
import ConvertibleNoteSafeOptionsSection from './ConvertibleNoteSafeOptionsSection';

const AddDraftInvestorModalBody: FC = () => {
  const { t } = useTranslate();

  const { data, actions } = useAddDraftInvestorModal();

  const isCapitalIncrease =
    data.draftType === draftTypes.DRAFT_CAPITAL_INCREASE;

  const translationSuffix = isCapitalIncrease ? '.FORCED' : '';

  const hasManualContribution = data.proportionalityType === '';

  const hasContributionDisabled =
    data.proportionalityType === proportionalityTypes.ND ||
    data.proportionalityType === proportionalityTypes.FD ||
    data.proportionalityType === proportionalityTypes.POTENTIAL;

  return (
    <Modal.Body>
      <PartnerSection />

      <div className="form-group">
        <label className="form-label" htmlFor="class-select-input">
          {t('Class')} <MandatoryField />
        </label>
        <div className="form-control-wrap">
          <CustomAsyncSelect
            id="class-select-input"
            value={data.selectedHoldingClassOption}
            onChange={actions.handleChangeHoldingClass}
            onFetchData={actions.handleSearchHoldingClassByTerm}
            placeholder={t('SearchClass')}
            minimumInputLength={1}
            noDataLabelKey="NoClassesHaveBeenFound"
            loadingLabelKey="LoadingClasses"
            errorLabelKey="ErrorLoadingClasses"
            defaultOptions={data.holdingClassOptions}
            isClearable={false}
          />
        </div>
      </div>

      {hasManualContribution ? (
        <div className="form-group">
          <label className="form-label" htmlFor="contribution-input">
            {t(`AmountOfTheContributionOf${translationSuffix}`)}{' '}
            <MandatoryField />
          </label>

          <div className="input-group">
            <div className="form-icon form-icon-left">
              <em className="icon ni ni-sign-euro" />
            </div>

            <NumberFormat
              id="contribution-input"
              className="form-control form-control-lg"
              value={data.contribution}
              onChange={actions.handleChangeContribution}
              thousandSeparator={numberFormat.thousandSeparator}
              decimalSeparator={numberFormat.decimalSeparator}
              allowNegative={false}
              decimalScale={0}
              disabled={hasContributionDisabled}
              required
            />
          </div>
        </div>
      ) : (
        <div className="form-group">
          <label className="form-label" htmlFor="proportionality-percent-input">
            {t('PercentageThatPartnerWillKeep')} <MandatoryField />
          </label>

          <div className="input-group">
            <div className="form-icon form-icon-left">
              <em className="icon ni ni-percent" />
            </div>

            <NumberFormat
              id="proportionality-percent-input"
              className="form-control form-control-lg"
              value={data.proportionalityPercent}
              onChange={actions.handleChangeProportionalityPercent}
              thousandSeparator={numberFormat.thousandSeparator}
              decimalSeparator={numberFormat.decimalSeparator}
              allowNegative={false}
              decimalScale={10}
              disabled={hasContributionDisabled}
              required
            />
          </div>
        </div>
      )}

      {isCapitalIncrease && <CapitalIncreaseOptionsSection />}

      <div className="form-group">
        <label className="form-label" htmlFor="discount-input">
          {t('Discount')} <MandatoryField />
        </label>

        <div className="input-group">
          <div className="form-icon form-icon-left">
            <em className="icon ni ni-percent" />
          </div>

          <NumberFormat
            id="discount-input"
            className="form-control form-control-lg"
            value={data.discountPercent}
            onChange={actions.handleChangeDiscountPercent}
            thousandSeparator={numberFormat.thousandSeparator}
            decimalSeparator={numberFormat.decimalSeparator}
            allowNegative={false}
            decimalScale={10}
            required
          />
        </div>

        {data.draftConditions?.discountPercent && (
          <span className="sub-text mt-1 mb-3">
            {t('TheGeneralDiscountHasBeen', {
              noteConditionsPercent: data.draftConditions.discountPercent,
            })}
          </span>
        )}
      </div>

      {isCapitalIncrease && (
        <div className="form-group row">
          <div className="col-6">
            <Radio
              id="by-valuation-discount-type-radio"
              name="discountType"
              label={t('ByValuation')}
              checked={data.hasDiscountByValuation}
              onChange={actions.handleChangeHasDiscountByValuation}
            />
          </div>

          <div className="col-6">
            <Radio
              id="by-share-discount-type-radio"
              name="discountType"
              label={t('ByShares')}
              checked={!data.hasDiscountByValuation}
              onChange={actions.handleChangeHasDiscountByValuation}
            />
          </div>
        </div>
      )}

      {!isCapitalIncrease && <ConvertibleNoteSafeOptionsSection />}
    </Modal.Body>
  );
};

export default AddDraftInvestorModalBody;
